import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getSelectedWorkerId } from "@/lib/workerStore";

function CheckInOption({
  label,
  description,
  Icon,
  active,
  activeBorder,
  activeBg,
  activeIcon,
  onClick,
  testId,
}) {
  const borderCls = active ? activeBorder : "border-slate-200";
  const bgCls = active ? activeBg : "bg-white";
  const iconCls = active ? activeIcon : "text-slate-400";
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full touch-btn h-20 rounded-xl flex items-center gap-4 px-5 border-2 transition-colors ${borderCls} ${bgCls}`}
    >
      <Icon className={`h-7 w-7 ${iconCls}`} />
      <div className="text-left">
        <p className="font-display text-lg font-bold text-slate-900">{label}</p>
        <p className="text-xs text-slate-600">{description}</p>
      </div>
    </button>
  );
}

export default function WorkerCheckIn() {
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [status, setStatus] = useState("no_issue");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const resolveWorker = useCallback(async () => {
    const wid = getSelectedWorkerId();
    if (!wid) {
      navigate("/worker", { replace: true });
      return;
    }
    try {
      const { data } = await api.get("/workers");
      const w = data.find((x) => x.id === wid);
      if (!w) navigate("/worker", { replace: true });
      else setWorker(w);
    } catch (err) {
      console.error("Employee lookup failed:", err);
      navigate("/worker", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    resolveWorker();
  }, [resolveWorker]);

  const submit = async () => {
    if (!worker) return;
    setBusy(true);
    try {
      await api.post("/checkins", { worker_id: worker.id, status, note });
      toast.success("Check-in submitted");
      navigate("/worker", { replace: true });
    } catch (err) {
      console.error("Check-in submit failed:", err);
      toast.error("Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (!worker) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="checkin-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Hand health
        </p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Quick check-in
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          For <span className="font-medium text-slate-900">{worker.name}</span>.
          No detailed medical info is stored.
        </p>
      </div>

      <div className="space-y-3">
        <CheckInOption
          label="No issue today"
          description="Hands feel normal."
          Icon={CheckCircle2}
          active={status === "no_issue"}
          activeBorder="border-emerald-600"
          activeBg="bg-emerald-50"
          activeIcon="text-emerald-600"
          onClick={() => setStatus("no_issue")}
          testId="checkin-no-issue-btn"
        />
        <CheckInOption
          label="Request supervisor follow-up"
          description="I'd like to speak with my supervisor."
          Icon={AlertTriangle}
          active={status === "follow_up"}
          activeBorder="border-orange-600"
          activeBg="bg-orange-50"
          activeIcon="text-orange-600"
          onClick={() => setStatus("follow_up")}
          testId="checkin-followup-btn"
        />
      </div>

      <Card className="p-4 border-slate-200">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
          Optional note
        </p>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Anything you want your supervisor to know…"
          data-testid="checkin-note-input"
        />
      </Card>

      <Button
        onClick={submit}
        disabled={busy}
        className="w-full touch-btn h-16 bg-slate-900 hover:bg-slate-800 text-white text-lg font-bold rounded-xl"
        data-testid="checkin-submit-btn"
      >
        {busy ? "Submitting…" : "Submit check-in"}
      </Button>
    </div>
  );
}
